import { useCallback, useEffect, useState } from "react";
import { cn } from "@/components/ui/utils";

interface DashboardCarouselProps {
  images: string[];
  className?: string;
}

const DashboardCarousel = ({ images, className }: DashboardCarouselProps) => {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (index === current || isTransitioning) return;

      setIsTransitioning(true);
      window.setTimeout(() => {
        setCurrent(index);
        window.setTimeout(() => setIsTransitioning(false), 50);
      }, 350);
    },
    [current, isTransitioning]
  );

  useEffect(() => {
    if (images.length < 2) return undefined;

    const timer = window.setInterval(() => {
      goTo((current + 1) % images.length);
    }, 5000);

    return () => window.clearInterval(timer);
  }, [current, goTo, images.length]);

  if (images.length === 0) return null;

  return (
    <div className={cn("relative", className)}>
      <div className="animate-float flex flex-row items-center gap-4">
        <div className="relative flex-1">
          {/* invisible sizer — keeps container height stable during transitions */}
          <img src={images[0]} aria-hidden className="invisible block h-auto w-full" />
          {images.map((image, index) => (
            <img
              key={image}
              src={image}
              alt={`ORbiT8 dashboard view ${index + 1}`}
              className={cn(
                "absolute inset-0 h-full w-full rounded-xl object-cover shadow-2xl shadow-black/10 transition-all duration-700 ease-in-out",
                index === current && !isTransitioning
                  ? "scale-[1.02] opacity-100"
                  : "scale-100 opacity-0"
              )}
              loading={index === 0 ? "eager" : "lazy"}
            />
          ))}
        </div>

        <div className="flex shrink-0 flex-col items-center justify-center gap-2">
          {images.map((image, index) => (
            <button
              key={image}
              type="button"
              onClick={() => goTo(index)}
              className={cn(
                "rounded-full transition-all duration-300",
                index === current
                  ? "h-6 w-2 bg-primary shadow-[0_0_8px_rgba(15,185,177,0.4)]"
                  : "h-2 w-2 bg-foreground/20 hover:bg-foreground/35"
              )}
              aria-label={`Show ORbiT8 dashboard view ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardCarousel;
